import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Package, Clock, CheckCircle2, RefreshCw, Truck, ArrowRight } from "lucide-react";

const statusConfig = {
  pending: { label: "Pendiente", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  confirmed: { label: "Confirmado", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle2 },
  refunded: { label: "Reembolsado", color: "bg-red-100 text-red-700 border-red-200", icon: RefreshCw },
  delivered: { label: "Entregado", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Package }
};

const deliveryConfig = {
  pending: { label: "Preparando", color: "bg-amber-100 text-amber-700" },
  shipped: { label: "Enviado", color: "bg-blue-100 text-blue-700" },
  delivered: { label: "Entregado", color: "bg-green-100 text-green-700" }
};

export default function Dashboard({ user, logout }) {
  const [participations, setParticipations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchParticipations();
  }, []);

  const fetchParticipations = async () => {
    try {
      const response = await axios.get(`${API}/participations`);
      setParticipations(response.data);
    } catch (error) {
      console.error("Error fetching participations:", error);
    } finally {
      setLoading(false);
    }
  };

  const activeParticipations = participations.filter(p => p.status === "pending");
  const confirmedParticipations = participations.filter(p => ["confirmed", "delivered"].includes(p.status));
  const refundedParticipations = participations.filter(p => p.status === "refunded");

  const ParticipationCard = ({ participation }) => {
    const { package: pkg } = participation;
    const status = statusConfig[participation.status] || statusConfig.pending;
    const StatusIcon = status.icon;

    return (
      <Card className="overflow-hidden card-shadow" data-testid={`participation-card-${participation.participation_id}`}>
        <div className="flex">
          <div className="w-32 h-32 flex-shrink-0">
            <img 
              src={pkg?.product_image} 
              alt={pkg?.product_name}
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="flex-1 p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold line-clamp-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {pkg?.product_name}
              </h3>
              <Badge className={status.color}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {status.label}
              </Badge>
            </div>
            
            <p className="text-sm text-muted-foreground mb-2">
              Precio pagado: <span className="font-medium text-foreground">{pkg?.group_price?.toFixed(2)}€</span>
            </p>

            {participation.delivery_status && (
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-4 w-4 text-muted-foreground" />
                <Badge variant="outline" className={deliveryConfig[participation.delivery_status]?.color}>
                  {deliveryConfig[participation.delivery_status]?.label}
                </Badge>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Unido: {new Date(participation.joined_at).toLocaleDateString('es-ES')}
            </p>

            <Link to={`/package/${pkg?.package_id}`}>
              <Button variant="ghost" size="sm" className="mt-2 -ml-2">
                Ver detalles
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <Header user={user} logout={logout} />
      
      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              ¡Hola, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground">
              Gestiona tus compras grupales desde aquí
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Activos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {activeParticipations.length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Confirmados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {confirmedParticipations.length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Reembolsados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {refundedParticipations.length}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Participations */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : participations.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No tienes compras grupales</h3>
                <p className="text-muted-foreground mb-4">
                  Explora las ofertas activas y únete a tu primera compra grupal
                </p>
                <Link to="/">
                  <Button data-testid="explore-deals-btn">
                    Ver ofertas
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Tabs defaultValue="all" className="space-y-6">
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all">
                  Todas ({participations.length})
                </TabsTrigger>
                <TabsTrigger value="active" data-testid="tab-active">
                  Activas ({activeParticipations.length})
                </TabsTrigger>
                <TabsTrigger value="confirmed" data-testid="tab-confirmed">
                  Confirmadas ({confirmedParticipations.length})
                </TabsTrigger>
                <TabsTrigger value="refunded" data-testid="tab-refunded">
                  Reembolsadas ({refundedParticipations.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-4">
                {participations.map(p => (
                  <ParticipationCard key={p.participation_id} participation={p} />
                ))}
              </TabsContent>

              <TabsContent value="active" className="space-y-4">
                {activeParticipations.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No tienes participaciones activas
                  </p>
                ) : (
                  activeParticipations.map(p => (
                    <ParticipationCard key={p.participation_id} participation={p} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="confirmed" className="space-y-4">
                {confirmedParticipations.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No tienes compras confirmadas
                  </p>
                ) : (
                  confirmedParticipations.map(p => (
                    <ParticipationCard key={p.participation_id} participation={p} />
                  ))
                )}
              </TabsContent>

              <TabsContent value="refunded" className="space-y-4">
                {refundedParticipations.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No tienes reembolsos
                  </p>
                ) : (
                  refundedParticipations.map(p => (
                    <ParticipationCard key={p.participation_id} participation={p} />
                  ))
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
    </div>
  );
}
